import { ref, computed, watch } from 'vue'
import { FUNCTION_TYPE } from '@/constants'
import store from '@/store'
import _ from 'lodash'

/**
 * 対象プレイスとの関係性
 */
export const PLACE_RELATIONSHIP = {
  IS: 'is', // いる
  CHECKIN: 'checkin', // チェックイン中
  DISTANCE: 'distance', // 距離比較
}

/**
 * 停止期間の比較演算子
 */
export const STILL_DAYS_OPERATOR = {
  GREATER: 'ge',
  LESS: 'le',
}

/**
 * センサー値の比較演算子
 */
export const SENSOR_VALUE_OPERATOR = {
  EQUAL: 'eq',
  GREATER: 'ge',
  LESS: 'le',
}

/**
 * 各条件カテゴリと権限の対応
 */
export const CONDITION_AVAILABLE_FUNCTION_TYPE = {
  userType: FUNCTION_TYPE.USER,
  search: FUNCTION_TYPE.USER,
  organization: FUNCTION_TYPE.USER,
  indoor: FUNCTION_TYPE.INDOOR,
  place: FUNCTION_TYPE.PLACE,
  status: FUNCTION_TYPE.STATUS,
  activity: FUNCTION_TYPE.COMMON,
  positioning: FUNCTION_TYPE.COMMON,
  sensor: FUNCTION_TYPE.SENSOR,
}

/**
 * 空の絞り込み条件取得
 * @returns {Object} 空の絞り込み条件
 */
const getBlankCondition = () => ({
  // ユーザータイプ
  userType: {
    target: null,
  },
  // キーワード検索条件
  search: {
    // キーワード
    keyword: null,
    // キーワード検索対象項目
    keywordTargets: [],
  },
  // グループ条件
  organization: {
    // 組織グループID
    organizationIds: null,
  },
  // 屋内条件
  indoor: {
    // サイトID
    indoorSiteIds: null,
    // ビルID
    indoorBuildingIds: null,
    // フロアID
    indoorLevelId: null,
  },
  // プレイス条件
  place: {
    // プレイスID
    placeId: null,
    // 対象プレイスとの関係性
    relationship: PLACE_RELATIONSHIP.IS,
    // プレイスからの距離(m)
    distance: null,
  },
  // ステイタス条件
  status: {
    // ステイタス種別ID
    statusTypeId: null,
  },
  // アクティビティ条件
  activity: {
    // 停止中のみの場合はtrue
    isStill: null,
    // 停止日数を指定可能な場合はtrue
    canSetDays: false,
    // 停止期間(日)
    stillDays: null,
    // 停止期間演算子(ge: 以上, le: 以内)
    stillDaysOperator: null,
  },
  // 測位状態条件
  positioning: {
    // 測位中のみの場合はtrue null はすべて扱い
    isPositioning: null,
  },
  // センサー値条件
  sensor: {
    // センサー測定項目ID
    sensorItemId: null,
    // センサー値を指定可能な場合はtrue
    canSetValue: false,
    // センサー値
    sensorValue: null,
    // センサー値演算子(eq: 等しい, ge: 以上, le: 以内)
    sensorValueOperator: null,
  },
})

/**
 * ユーザーリストの絞り込み条件の保持・管理
 * @param {Number} placeId 初期プレイスID
 * @param {Number} indoorlevelId 初期フロアID
 */
export function useUserStatusFilterCondition({ placeId, indoorLevelId } = {}) {
  /**
   * 絞り込み条件
   * @type {Ref.<Object>}
   */
  const condition = ref(getBlankCondition())

  /**
   * アクティビティの詳細基準条件(停止日数指定)が有効な場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const isActivityCriteriaAvailable = computed(() => {
    const { canSetDays, stillDays, stillDaysOperator } =
      condition.value.activity
    return canSetDays && stillDays != null && stillDaysOperator
  })

  /**
   * センサーの詳細基準条件(値指定)が有効な場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const isSensorCritetiaAvailable = computed(() => {
    const { canSetValue, sensorValue, sensorValueOperator } =
      condition.value.sensor
    return canSetValue && sensorValue != null && sensorValueOperator
  })

  /**
   * リクエスト用絞り込み条件(リクエストに乗らない条件は初期化したもの)
   * @type {ComputedRef.<Object>}
   */
  const conditionForRequest = computed(() => {
    const blankCondition = getBlankCondition()
    const result = _.cloneDeep(condition.value)

    // search - キーワード未入力時はカテゴリ初期化
    if (!condition.value.search.keyword) {
      result.search = blankCondition.search
    }

    // indoor - サイトは初期化
    result.indoor.indoorSiteIds = blankCondition.indoor.indoorSiteIds

    // activity - 停止日数を指定できない時は日数項目は初期化
    if (!isActivityCriteriaAvailable.value) {
      result.activity = {
        ...blankCondition.activity,
        isStill: result.activity.isStill,
      }
    }

    // sensor - 値を指定できない時は値項目は初期化
    if (!isSensorCritetiaAvailable.value) {
      result.sensor = {
        ...blankCondition.sensor,
        sensorItemId: result.sensor.sensorItemId,
      }
    }

    return result
  })

  /**
   * condition を更新する
   * @param {Object} value 更新値
   */
  const updateCondition = (value) => {
    for (const [k, v] of Object.entries(value)) {
      if (k in condition.value) {
        condition.value[k] = {
          ...condition.value[k],
          ...v,
        }
      }
    }
  }

  /**
   * 引数を反映する
   * @param {Number} placeId プレイスID
   * @param {Number} indoorLevelId フロアID
   */
  const applyParameter = ({ placeId, indoorLevelId }, resetOther) => {
    if (!placeId) return

    updateCondition({
      ...(resetOther ? getBlankCondition() : {}),
      place: {
        placeId,
        relationship: PLACE_RELATIONSHIP.DISTANCE,
        distance: indoorLevelId ? 5 : 5000,
      },
    })
  }

  /**
   * 地図の屋内状態を反映する
   */
  const applyMapIndoorState = () => {
    const activeIndoorBuildingId =
      store.getters['mapIndoor/activeIndoorBuildingId']
    const activeIndoorLevelId = store.getters['mapIndoor/activeIndoorLevelId']

    updateCondition({
      indoor: {
        indoorBuildingIds: activeIndoorBuildingId
          ? [activeIndoorBuildingId]
          : condition.value.indoor.indoorBuildingIds,
        indoorLevelId: activeIndoorLevelId,
      },
    })
  }

  // 変更を store に反映
  // 中身の変更の無い shallow copy のみの変更を省くため文字列化して watch
  watch(
    () => JSON.stringify(condition.value),
    () => {
      store.commit('member/setCondition', condition.value)
    }
  )

  /**
   * 初期絞り込み条件設定
   */
  // 測位状態は測位中にする
  condition.value.positioning.isPositioning = true
  if (placeId) {
    // 引数を処理
    applyParameter({ placeId, indoorLevelId })
  } else {
    // store の状態反映
    updateCondition(store.getters['member/condition'])
  }
  // キーワード検索対象は store の値を使用
  condition.value.search.keywordTargets =
    store.getters['userKeywordTarget/keywordTargets']
  // 屋内状態を同期
  applyMapIndoorState()

  return {
    condition,
    conditionForRequest,
    updateCondition,
    getBlankCondition,
    applyParameter,
    applyMapIndoorState,
  }
}
